import Swal from 'sweetalert2';

// ── Theme tokens (mirrors src/theme.js) ──────────────────────────
const PRIMARY   = '#1565C0';
const SECONDARY = '#0d47a1';
const SUCCESS   = '#2e7d32';
const ERROR     = '#d32f2f';
const WARNING   = '#ed6c02';
const NEUTRAL   = '#5f6b7a';

// Shared visual base — keeps every dialog consistent with the app theme.
const BASE = {
  buttonsStyling: false,
  customClass: {
    popup:      'sw-popup',
    title:      'sw-title',
    htmlContainer: 'sw-body',
    actions:    'sw-actions',
    confirmButton: 'sw-btn sw-btn-confirm',
    cancelButton:  'sw-btn sw-btn-cancel',
    denyButton:    'sw-btn sw-btn-deny',
    icon:       'sw-icon',
    input:      'sw-input',
  },
  reverseButtons: true,
  showClass: { popup: 'sw-anim-in' },
  hideClass: { popup: 'sw-anim-out' },
};

// Inject the styles once — keeps the look on-brand without a separate CSS file.
let injected = false;
function injectStyles() {
  if (injected || typeof document === 'undefined') return;
  injected = true;
  const css = `
    /* Sit above MUI Dialog (zIndex 1300) and Snackbar (1400) so confirms /
       toasts always appear on top of any open form dialog. */
    .swal2-container { z-index: 2000 !important; }
    .swal2-container.swal2-backdrop-show { background: rgba(15, 23, 42, 0.55) !important; }

    .sw-popup {
      border-radius: 14px;
      padding: 1.25rem 1.5rem 1.5rem;
      box-shadow: 0 24px 60px rgba(0,0,0,0.28), 0 6px 20px rgba(0,0,0,0.12);
      font-family: 'Inter', 'Roboto', sans-serif;
    }
    .sw-title {
      font-weight: 800; font-size: 1.2rem; color: #0d47a1;
      letter-spacing: -0.2px;
    }
    .sw-body {
      font-size: 0.92rem; color: #475569; line-height: 1.55;
    }
    .sw-icon { margin-top: 0.5rem !important; }
    .sw-actions {
      gap: 0.6rem;
      margin-top: 1.2rem !important;
    }
    .sw-btn {
      font-weight: 700; text-transform: none; letter-spacing: 0.2px;
      padding: 0.55rem 1.2rem; border-radius: 8px;
      border: 1px solid transparent;
      font-size: 0.88rem; cursor: pointer;
      transition: transform 0.12s, box-shadow 0.12s, background 0.12s;
    }
    .sw-btn:hover { transform: translateY(-1px); }
    .sw-btn-confirm {
      background: linear-gradient(135deg, ${PRIMARY} 0%, ${SECONDARY} 100%);
      color: #fff;
      box-shadow: 0 3px 10px rgba(21,101,192,0.28);
    }
    .sw-btn-confirm:hover { box-shadow: 0 5px 16px rgba(21,101,192,0.4); }
    .sw-btn-confirm.sw-confirm-success {
      background: linear-gradient(135deg, ${SUCCESS} 0%, #1b5e20 100%);
      box-shadow: 0 3px 10px rgba(46,125,50,0.28);
    }
    .sw-btn-confirm.sw-confirm-danger {
      background: linear-gradient(135deg, ${ERROR} 0%, #b71c1c 100%);
      box-shadow: 0 3px 10px rgba(211,47,47,0.28);
    }
    .sw-btn-confirm.sw-confirm-warning {
      background: linear-gradient(135deg, ${WARNING} 0%, #d84315 100%);
      box-shadow: 0 3px 10px rgba(237,108,2,0.28);
    }
    .sw-btn-cancel, .sw-btn-deny {
      background: #fff; color: ${NEUTRAL};
      border-color: #d6dbe0;
    }
    .sw-btn-cancel:hover, .sw-btn-deny:hover { background: #f5f6fa; }
    .sw-input {
      border-radius: 8px !important;
      border: 1px solid #d6dbe0 !important;
      box-shadow: none !important;
      padding: 0.65rem 0.9rem !important;
      font-size: 0.95rem !important;
    }
    .sw-input:focus { border-color: ${PRIMARY} !important; }
    .swal2-toast.sw-popup { padding: 0.6rem 1rem; }
    @keyframes sw-pop-in { from { transform: scale(0.92); opacity: 0; } to { transform: scale(1); opacity: 1; } }
    @keyframes sw-pop-out { from { transform: scale(1); opacity: 1; } to { transform: scale(0.96); opacity: 0; } }
    .sw-anim-in  { animation: sw-pop-in  0.18s ease-out; }
    .sw-anim-out { animation: sw-pop-out 0.14s ease-in; }
  `;
  const style = document.createElement('style');
  style.id = 'sw-theme';
  style.textContent = css;
  document.head.appendChild(style);
}
injectStyles();

// ── Toast (top-right, auto-dismiss) ──────────────────────────────
const Toast = Swal.mixin({
  ...BASE,
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 2800,
  timerProgressBar: true,
  didOpen: (t) => {
    t.addEventListener('mouseenter', Swal.stopTimer);
    t.addEventListener('mouseleave', Swal.resumeTimer);
  },
});

// Quick toast helpers
export const success = (text, title = 'Success')   => Toast.fire({ icon: 'success', title, text });
export const info    = (text, title = 'Heads up')   => Toast.fire({ icon: 'info',    title, text });
export const warning = (text, title = 'Warning')    => Toast.fire({ icon: 'warning', title, text });

// Error gets a prominent modal (so users actually read it)
export function error(text, title = 'Something went wrong') {
  return Swal.fire({
    ...BASE,
    icon: 'error', title,
    text: typeof text === 'string' ? text : (text?.message || 'Please try again.'),
    confirmButtonText: 'OK',
  });
}

// A blocking modal that can carry markup — a list of what is wrong,
// rather than one run-on sentence. `error()` deliberately takes only
// text, because most callers hand it a raw server message and markup
// there would be an injection waiting to happen; this one is for copy
// the caller wrote itself.
export function alertHtml({ title = 'Please check this', html = '', icon = 'warning',
                            confirmText = 'OK' } = {}) {
  return Swal.fire({
    ...BASE,
    icon, title, html,
    confirmButtonText: confirmText,
  });
}

// Confirmation prompt with optional danger/warning styling.
// Returns true if confirmed, false otherwise.
export async function confirm({
  title = 'Are you sure?',
  text = '',
  confirmText = 'Yes, continue',
  cancelText = 'Cancel',
  icon = 'question',
  tone = 'primary',  // 'primary' | 'danger' | 'warning' | 'success'
  html = null,
} = {}) {
  const klass =
    tone === 'danger'  ? 'sw-btn sw-btn-confirm sw-confirm-danger'
    : tone === 'warning' ? 'sw-btn sw-btn-confirm sw-confirm-warning'
    : tone === 'success' ? 'sw-btn sw-btn-confirm sw-confirm-success'
    : 'sw-btn sw-btn-confirm';

  const res = await Swal.fire({
    ...BASE,
    customClass: { ...BASE.customClass, confirmButton: klass },
    icon,
    title,
    ...(html ? { html } : { text }),
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: cancelText,
    focusCancel: tone === 'danger',
  });
  return res.isConfirmed;
}

// Convenience: standardized "delete record" confirm → returns boolean
export const confirmDelete = (label = 'this record') =>
  confirm({
    title: 'Confirm delete',
    text:  `You are about to delete ${label}. This cannot be undone.`,
    confirmText: 'Yes, delete',
    cancelText:  'Keep it',
    icon: 'warning',
    tone: 'danger',
  });

// Prompt for a single-line text input. Returns the entered value (string) or null if cancelled.
export async function prompt({
  title = 'Enter value',
  text = '',
  inputLabel = '',
  inputPlaceholder = '',
  inputType = 'text',  // 'text' | 'textarea' | 'password' | 'email' | 'number'
  initialValue = '',
  confirmText = 'Save',
  cancelText  = 'Cancel',
  validate,
} = {}) {
  const res = await Swal.fire({
    ...BASE,
    title,
    text: text || undefined,
    input: inputType,
    inputLabel,
    inputPlaceholder,
    inputValue: initialValue,
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: cancelText,
    inputValidator: validate
      ? (value) => validate(value) || undefined
      : undefined,
  });
  return res.isConfirmed ? res.value : null;
}

// Pull a friendly message out of an axios error (or any error).
export function pickError(err, fallback = 'Operation failed') {
  if (!err) return fallback;
  return err.response?.data?.message || err.message || fallback;
}

const swal = { success, info, warning, error, alertHtml, confirm, confirmDelete, prompt, pickError };
export default swal;
